import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import LandingHeader from '../components/landing/LandingHeader';
import LandingFooter from '../components/landing/LandingFooter';

/**
 * Shared renderer for legal documents (Privacy Policy, Terms of Service).
 * Loads markdown from /legal/<file>.md and renders it in a readable prose
 * layout, wrapped in the public landing chrome.
 *
 * Props:
 *   file  - markdown filename without extension (e.g. 'privacy-policy')
 *   logLabel - human label used in console errors
 */
export default function LegalPage({ file, logLabel }) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL || ''}/legal/${file}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        // Guard against the SPA fallback returning index.html instead of markdown.
        if (/^\s*<!doctype html/i.test(text)) throw new Error('document not found');
        if (active) {
          setContent(text);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error(`Failed to load ${logLabel}:`, err);
        if (active) {
          setError(t('legal.errorLoading'));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [file, logLabel, t]);

  return (
    <Layout>
      <LandingHeader />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link
          to="/"
          className="text-accent-primary hover:text-accent-secondary mb-8 inline-block text-sm font-medium"
        >
          ← {t('common.back')}
        </Link>

        {loading && (
          <div className="animate-pulse" aria-busy="true">
            <div className="h-9 bg-bg-tertiary rounded w-3/4 mb-4" />
            <div className="h-4 bg-bg-tertiary rounded w-1/2 mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-bg-tertiary rounded" />
              <div className="h-4 bg-bg-tertiary rounded" />
              <div className="h-4 bg-bg-tertiary rounded w-5/6" />
            </div>
          </div>
        )}

        {error && (
          <div className="bg-status-error/10 border border-status-error text-status-error px-4 py-3 rounded-xl">
            <p>{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 text-sm underline hover:no-underline"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {!loading && !error && (
          <article className="legal-prose max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        )}
      </main>
      <LandingFooter />
    </Layout>
  );
}
